import { useSetAtom } from 'jotai';

import { getHttpServerErrorAwareRefetchInterval } from '@/adapters/query';
import {
  ScreenerSettingName,
  useIsScreenerSettingEnabled
} from '@/hooks/screener-settings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSelectedSpecificFacilityId } from '@/hooks/useFacilitiesQuery';
import {
  getPccSyncNeedsReviewAlertPanelDismissalKey,
  pccSyncNeedsReviewAlertDismissalsAtom
} from '@/pages/CRM/ScreenerResidents/PointClickCareUpdates/NeedsReviewAlertPanel/pccSyncNeedsReviewAlertDismissalsAtom';

import { useIntegrationEventsCountActiveQuery } from './useIntegrationEventsCountActiveQuery';

const PCC_NEEDS_REVIEW_COUNT_REFETCH_INTERVAL_MS = 60 * 60 * 1000;

export const usePccNeedsReviewCount = () => {
  const facilityId = useSelectedSpecificFacilityId();
  const userId = useCurrentUser().data?.id ?? '';
  const setDismissals = useSetAtom(pccSyncNeedsReviewAlertDismissalsAtom);
  const isPccSyncV2Enabled = useIsScreenerSettingEnabled(
    ScreenerSettingName.PccSyncV2Enabled,
    facilityId ?? ''
  );
  const isEnabled = !!facilityId && isPccSyncV2Enabled;

  const { data } = useIntegrationEventsCountActiveQuery(
    { facilityId: facilityId ?? '' },
    {
      enabled: isEnabled,
      refetchInterval: (_data, query) =>
        getHttpServerErrorAwareRefetchInterval(
          PCC_NEEDS_REVIEW_COUNT_REFETCH_INTERVAL_MS,
          query
        ),
      onSuccess: (count) => {
        if (!facilityId) {
          return;
        }
        const key = getPccSyncNeedsReviewAlertPanelDismissalKey(
          userId,
          facilityId
        );
        setDismissals((previous) => {
          const dismissedAtCount = previous[key];
          if (dismissedAtCount === undefined || count === dismissedAtCount) {
            return previous;
          }
          if (count < dismissedAtCount) {
            return { ...previous, [key]: count };
          }
          const { [key]: _, ...rest } = previous;
          return rest;
        });
      }
    }
  );

  return {
    facilityId,
    needsReviewCount: isEnabled ? (data ?? 0) : 0
  };
};
